import { useAsyncEffect, useMounted } from '@fullstory/react-hooks';
import { toPng } from 'html-to-image';
import { useCallback, useEffect, useState } from 'react';
import { CONTAINER_CLASS, IGNORE_FILTER_CLASS, SCROLL_CLASS } from './constants';
import css from './ElementToImage.module.scss';
import { BoxStyleSpaceScale, SpaceScale } from '@fullstory/components';

/**
 * Subset of the options available in html-to-image
 *
 * @see https://github.com/bubkoo/html-to-image?tab=readme-ov-file#options
 */
interface HtmlToImageOptions {
  skipFonts?: boolean;
  skipAutoScale?: boolean;
  backgroundColor?: string;
  pixelRatio?: number;
  canvasHeight?: number;
  canvasWidth?: number;
}

export interface UseElementToImageProps {
  fileName?: string;
  padding?: SpaceScale | BoxStyleSpaceScale;
  fitContent?: boolean;
  config?: HtmlToImageOptions;
}

interface State {
  error: boolean;
  data: string | null;
  loading: boolean;
  lastRequested: number;
  lastDownloaded: number;
}

function optOutFilter(node: HTMLElement) {
  return node?.classList?.contains(IGNORE_FILTER_CLASS) ? false : true;
}

/**
 * Hook to convert an element to an image. Returns a setRef callback, a method to start
 * the image generation, and some state including the image data, loading state, and error state.
 * The hook will automatically download the file if "file name" is provided.
 *
 * This hook can also be used without the component wrappers. Note that the when doing so you should:
 * - use setElementRef on the element you want to capture in the image
 * - add "containerClass" to the parent of the target if you want to hide changes to the underlying element
 * - use "ignoreClass" on any children of the target that should not be included in the image
 *
 * @param fileName optional file name. If provided, the image will be downloaded automatically
 * @param padding optional padding to add to the element before converting to an image, @default 16
 * @param fitContent optional flag to fit the image to the contents of the element / remove overflow. This is
 *                    achieved by overriding the target width's to match the scroll width @default false
 * @param config optional html-to-image config
 */
export function useElementToImage({
  fileName,
  padding = 16,
  fitContent = false,
  config = {},
}: UseElementToImageProps) {
  const [elementRef, setElementRef] = useState<HTMLElement | null>(null);
  const [requestCount, setRequestCount] = useState(0);
  const isMounted = useMounted();

  const [state, setState] = useState<State>({
    error: false,
    data: null,
    loading: false,
    lastRequested: 0,
    lastDownloaded: 0,
  });

  useEffect(() => {
    if (fileName && state.lastDownloaded !== state.lastRequested && state.data) {
      const downloadLink = document.createElement('a');
      downloadLink.download = fileName;
      downloadLink.href = state.data;
      downloadLink.click();
      downloadLink.remove();
      setState(prev => ({
        ...prev,
        lastDownloaded: state.lastRequested,
      }));
    }
  }, [fileName, state.data, state.lastDownloaded, state.lastRequested]);

  useAsyncEffect(
    async isActive => {
      if (!isActive() || requestCount === state.lastRequested || !elementRef) {
        return;
      }

      setState(prev => ({
        ...prev,
        loading: true,
        lastRequested: requestCount,
      }));

      try {
        // wait for the next frame, this allows the loading variable to propegate
        await animationFrameWithDelay();

        const container = elementRef.closest(`.${CONTAINER_CLASS}`) as HTMLElement | null;
        // stop jitter by setting the parent element to the size of the child element with hidden overflow
        if (container) {
          const boundingRect = container.getBoundingClientRect();
          container.style.setProperty('width', `${boundingRect.width}px`);
          container.style.setProperty('height', `${boundingRect.height}px`);
          container.style.setProperty('overflow', 'hidden');
        }
        elementRef.classList.add(css.downloading);

        if (padding !== undefined) {
          const paddingValue = Array.isArray(padding)
            ? padding.reduce((acc, p) => `${acc} ${p}px`, '')
            : `${padding}px`;
          elementRef.style.setProperty('padding', paddingValue.trim());
        }

        if (fitContent) {
          const scrollElement = elementRef.querySelector(`.${SCROLL_CLASS}`);

          if (scrollElement) {
            let width = scrollElement.scrollWidth;
            // calculate padding and margin from parents
            let parent = scrollElement.parentElement;
            while (parent && parent !== elementRef) {
              width += computeAdditionalHorizontalSpace(parent);
              parent = parent.parentElement;
            }

            // lastly factor in current element padding
            width += computeAdditionalHorizontalSpace(elementRef);
            elementRef.style.setProperty('width', `${width}px`);
          } else if (elementRef.scrollWidth > elementRef.clientWidth) {
            elementRef.style.setProperty('width', `${elementRef.scrollWidth}px`);
          }

          // allow some time for any width dependant hooks to run
          await animationFrameWithDelay(250);
        }

        const data = await toPng(elementRef, {
          ...config,
          filter: optOutFilter,
        });

        // style cleanup
        elementRef.style.removeProperty('padding');
        elementRef.style.removeProperty('width');

        if (container) {
          container.style.removeProperty('width');
          container.style.removeProperty('height');
          container.style.removeProperty('overflow');
        }
        elementRef.classList.remove(css.downloading);

        if (isActive()) {
          setState(prev => ({
            ...prev,
            loading: false,
            data,
            error: false,
          }));
        }
      } catch (e) {
        console.error('Error converting element to image', e);
        if (isActive()) {
          setState(prev => ({
            ...prev,
            loading: false,
            data: null,
            error: true,
          }));
        }
      }

      return () => {
        if (isMounted()) {
          setState(prev => ({
            ...prev,
            loading: false,
            data: null,
            error: false,
          }));
        }
      };
    },
    [requestCount, elementRef, padding, fitContent],
  );

  const generatePng = useCallback(() => {
    setRequestCount(prev => prev + 1);
  }, []);

  return {
    setElementRef,
    containerClass: CONTAINER_CLASS,
    ignoreClass: IGNORE_FILTER_CLASS,
    scrollClass: SCROLL_CLASS,
    generatePng,
    data: state.data,
    loading: state.loading,
    error: state.error,
  };
}

function animationFrameWithDelay(delay: number = 0) {
  return new Promise(resolve => {
    if (delay) {
      setTimeout(() => {
        window.requestAnimationFrame(resolve);
      }, delay);
    } else {
      window.requestAnimationFrame(resolve);
    }
  });
}

function parsePixelValue(value: string): number {
  if (value) {
    const parsed = parseInt(value.replace('px', ''), 10);
    if (!isNaN(parsed)) {
      return parsed;
    }
  }
  return 0;
}

function computeAdditionalHorizontalSpace(element: HTMLElement): number {
  const computedStyle = getComputedStyle(element);
  const paddingLeft = parsePixelValue(computedStyle.paddingLeft);
  const paddingRight = parsePixelValue(computedStyle.paddingRight);
  const marginLeft = parsePixelValue(computedStyle.marginLeft);
  const marginRight = parsePixelValue(computedStyle.marginRight);
  const borderLeft = parsePixelValue(computedStyle.borderLeftWidth);
  const borderRight = parsePixelValue(computedStyle.borderRightWidth);
  return paddingLeft + paddingRight + marginLeft + marginRight + borderLeft + borderRight;
}
